/**
 * The Service side's whole route tree, in one default export.
 *
 * THIS FILE IS THE BUNDLE BOUNDARY. App.tsx imports it with React.lazy and
 * nothing else on the Materials side imports anything under src/service, so
 * Vite compiles everything reachable from here into its own chunk. A store-man
 * who signs in and never touches Service downloads none of it.
 *
 * Two rules keep that true, and scripts/check-module-boundary.py enforces both:
 *   1. nothing outside src/service may import from src/service, except
 *      App.tsx, which imports exactly this file, lazily
 *   2. nothing inside src/service may import from src/pages
 *
 * Shared things - components/ui, lib/auth, lib/data, lib/format - are fair game
 * for both sides. They are the frame, not either room.
 */
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { EmptyState } from '@/components/ui'
import { ShieldOff } from 'lucide-react'
import { mayUseModule } from '@/lib/modules'
import { ServiceShell } from './ServiceShell'
import { canSeeServiceRoute } from './nav'
import ServiceDashboard from './pages/ServiceDashboard'
import { WorkOrderDetail, WorkOrderList } from './pages/WorkOrders'
import WorkOrderBuilder from './pages/WorkOrderBuilder'
import ScheduleOfRates from './pages/ScheduleOfRates'
import ServiceMasters from './pages/ServiceMasters'
import ServiceSettings from './pages/ServiceSettings'
import { MeasurementDetail, MeasurementList } from './pages/MeasurementBook'
import MeasurementEntry from './pages/MeasurementEntry'
import { HireLogDetail, HireLogList } from './pages/HireLogs'
import { RaBillDetail, RaBillList } from './pages/RaBills'
import ContractorPayables, { ContractorPayableDetail } from './pages/ContractorPayables'
import ContractorPayments from './pages/ContractorPayments'
import { Statutory } from './pages/Statutory'
import ReverseCharge from './pages/ReverseCharge'
import { RetentionReleaseDetail, RetentionReleaseList } from './pages/RetentionReleases'
import { AdvanceDetail, AdvanceList } from './pages/Advances'
import { CreditNoteDetail, CreditNoteList } from './pages/CreditNotes'
import { VariationDetail, VariationEditor, VariationList } from './pages/Variations'

/**
 * A screen this person may not open.
 *
 * Shown rather than redirected, deliberately. A silent bounce to the dashboard
 * makes somebody think the link is broken and click it four more times; a
 * sentence tells them it is a boundary and who to ask.
 */
function NotForYou({ what }: { what: string }) {
  return (
    <EmptyState
      icon={<ShieldOff className="h-8 w-8" />}
      title="Not open to your role"
      message={`${what} is not part of what your role does. If you think it should be, ask the System Administrator — it is decided by your role, not by this screen.`}
    />
  )
}

function Gate({ to, what, children }: { to: string; what: string; children: React.ReactNode }) {
  const { profile } = useAuth()
  return canSeeServiceRoute(profile?.role, to) ? <>{children}</> : <NotForYou what={what} />
}

export default function ServiceApp() {
  const { profile } = useAuth()

  // The whole side, gated once. A store-man who types /service into the bar
  // lands back on his own dashboard rather than on an empty amber shell.
  if (!mayUseModule(profile?.role, 'service')) {
    return <Navigate to="/" replace />
  }

  return (
    <Routes>
      <Route element={<ServiceShell />}>
        <Route index element={<ServiceDashboard />} />

        <Route path="work-orders" element={
          <Gate to="/service/work-orders" what="Work Orders"><WorkOrderList /></Gate>} />
        {/* BEFORE the :id route, or "new" is read as an order number. */}
        <Route path="work-orders/new" element={
          <Gate to="/service/work-orders" what="Work Orders"><WorkOrderBuilder /></Gate>} />
        <Route path="work-orders/:id/edit" element={
          <Gate to="/service/work-orders" what="Work Orders"><WorkOrderBuilder /></Gate>} />
        <Route path="work-orders/:id" element={
          <Gate to="/service/work-orders" what="Work Orders"><WorkOrderDetail /></Gate>} />

        <Route path="variations" element={
          <Gate to="/service/variations" what="Variation Orders"><VariationList /></Gate>} />
        {/* BEFORE the :id route, as everywhere else on this side. */}
        <Route path="variations/:id/edit" element={
          <Gate to="/service/variations" what="Variation Orders"><VariationEditor /></Gate>} />
        <Route path="variations/:id" element={
          <Gate to="/service/variations" what="Variation Orders"><VariationDetail /></Gate>} />
        <Route path="work-items" element={
          <Gate to="/service/work-items" what="The Schedule of Rates"><ScheduleOfRates /></Gate>} />
        <Route path="measurements" element={
          <Gate to="/service/measurements" what="The Measurement Book"><MeasurementList /></Gate>} />
        {/* BEFORE the :id route, or "new" is read as a page number. */}
        <Route path="measurements/new" element={
          <Gate to="/service/measurements" what="The Measurement Book"><MeasurementEntry /></Gate>} />
        <Route path="measurements/:id/edit" element={
          <Gate to="/service/measurements" what="The Measurement Book"><MeasurementEntry /></Gate>} />
        <Route path="measurements/:id" element={
          <Gate to="/service/measurements" what="The Measurement Book"><MeasurementDetail /></Gate>} />

        {/* 0214: the hire log sheet - the Measurement Book's twin for machines on hire. */}
        <Route path="hire-logs" element={
          <Gate to="/service/hire-logs" what="Hire Log Sheets"><HireLogList /></Gate>} />
        <Route path="hire-logs/:id" element={
          <Gate to="/service/hire-logs" what="Hire Log Sheets"><HireLogDetail /></Gate>} />

        <Route path="ra-bills" element={
          <Gate to="/service/ra-bills" what="RA Bills"><RaBillList /></Gate>} />
        <Route path="ra-bills/:id" element={
          <Gate to="/service/ra-bills" what="RA Bills"><RaBillDetail /></Gate>} />
        <Route path="contractor-payables" element={
          <Gate to="/service/contractor-payables" what="Contractor Payables"><ContractorPayables /></Gate>} />
        <Route path="contractor-payables/:id" element={
          <Gate to="/service/contractor-payables" what="Contractor Payables"><ContractorPayableDetail /></Gate>} />
        <Route path="payments" element={
          <Gate to="/service/payments" what="Payments to Contractors"><ContractorPayments /></Gate>} />
        <Route path="statutory" element={
          <Gate to="/service/statutory" what="Statutory &amp; Deductions"><Statutory /></Gate>} />

        <Route path="reverse-charge" element={
          <Gate to="/service/reverse-charge" what="Reverse charge"><ReverseCharge /></Gate>} />
        <Route path="credit-notes" element={
          <Gate to="/service/credit-notes" what="Credit Notes">
            <CreditNoteList /></Gate>} />
        <Route path="credit-notes/:id" element={
          <Gate to="/service/credit-notes" what="Credit Notes">
            <CreditNoteDetail /></Gate>} />
        <Route path="advances" element={
          <Gate to="/service/advances" what="Mobilisation Advances">
            <AdvanceList /></Gate>} />
        <Route path="advances/:id" element={
          <Gate to="/service/advances" what="Mobilisation Advances">
            <AdvanceDetail /></Gate>} />
        <Route path="retention-releases" element={
          <Gate to="/service/retention-releases" what="Retention Releases">
            <RetentionReleaseList /></Gate>} />
        <Route path="retention-releases/:id" element={
          <Gate to="/service/retention-releases" what="Retention Releases">
            <RetentionReleaseDetail /></Gate>} />

        <Route path="masters" element={
          <Gate to="/service/masters" what="Masters"><ServiceMasters /></Gate>} />
        <Route path="settings" element={
          <Gate to="/service/settings" what="Service settings"><ServiceSettings /></Gate>} />

        {/* Anything else under /service goes to the Service dashboard, not to
            the Materials one - a wrong turn should not throw you out of the
            building. */}
        <Route path="*" element={<Navigate to="/service" replace />} />
      </Route>
    </Routes>
  )
}
